import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import { Link, useActionData, useSearchParams } from '@remix-run/react'
import { ActionFunction, json } from '@remix-run/server-runtime'
import { withZod } from '@remix-validated-form/with-zod'
import moment from 'moment'
import { useState } from 'react'
import { v4 } from 'uuid'
import { z } from 'zod'
import FormInput from 'app/components/form/FormInput'
import { SmartForm } from 'app/components/form/SmartForm'
import { User } from 'app/models/User'
import { envName } from 'lib/envName'
import { debug, logError } from 'lib/logging'
import { sendTemplateEmail, SENDGRID_TEMPLATES } from 'lib/sendEmail.server'

let validator = withZod(
  z.object({ email: z.string().min(1, { message: `Email is required` }).email(`Must be a valid email`) }),
)

export const action: ActionFunction = async ({ request }) => {
  try {
    let formData = await request.formData()
    let email = formData.get(`email`) as string
    debug(`user`, `forgot password submit`, email)

    let user = await User.getByEmail(email, null)
    if (user) {
      user.resetCode = v4()
      user.resetCodeExpires = moment().add(10, `minutes`).toDate()
      await user.save()
      let url = `https://${envName()}.trackable.io/reset?code=${user.resetCode}&email=${encodeURIComponent(user.email)}`
      await sendTemplateEmail([user.email], SENDGRID_TEMPLATES.PASSWORD_RESET, { reset_link: url })
    }
  } catch (err: any) {
    logError(`something awful happened`, err)
    return json({ success: false, error: err.message })
  }
  return json({ success: true })
}

export default function Forgot() {
  let [searchParams] = useSearchParams()
  let [email, setEmail] = useState(decodeURIComponent(searchParams.get(`email`) || ``))
  let actionData = useActionData<typeof action>()

  if (actionData?.error) {
    return (
      <div className="grid h-screen place-items-center bg-slate-50">
        <div className="inline-block w-[800px] max-w-[95vw] rounded-md border border-trn-200 bg-white p-10 shadow">
          <h3 className="mb-5 text-center">Something went wrong</h3>
          <p className="mb-5 text-center italic text-slate-800">{actionData.error}</p>
          <p className="text-center text-slate-800">
            You may have better success by requesting{` `}
            {/* eslint-disable-next-line prettier/prettier */}
            <a className="btn btn-link inline" href="/forgot">
              a new password reset
            </a>
            .
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="grid h-screen place-items-center bg-slate-50">
      <div className="inline-block w-[600px] max-w-[95vw] rounded-md border border-trn-200 bg-white p-20 shadow">
        {actionData?.success ? (
          <>
            <div className="mb-1 block">
              If you have an account with us, you will receive an email with password reset instructions.
            </div>
            <Link to="/login" className="mt-5 block text-center font-bold">
              <ArrowLeftIcon className="inline h-5 w-5" /> Back to login
            </Link>
          </>
        ) : (
          <SmartForm validator={validator} method="post" hideButtons={true} className="w-full">
            <div className="col-span-6">
              <h2 className="text-center">Forgot password?</h2>
              <p className="mb-10 mt-2 text-center text-slate-800">No worries, we'll send you reset instructions.</p>
              <FormInput
                cols={6}
                label="Email"
                className="mb-5 text-center"
                labelClassName="text-ui-lg mb-2"
                type="text"
                name="email"
                value={email}
                placeholder="Enter your email"
                onChange={(event) => setEmail(event.currentTarget.value)}
              />
              <button className="btn btn-primary btn-lg col-span-6 mb-5 w-full">Reset Password</button>
              <Link to="/login" className="block text-center font-bold">
                <ArrowLeftIcon className="inline h-5 w-5" /> Back to login
              </Link>
            </div>
          </SmartForm>
        )}
      </div>
    </div>
  )
}
